var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Brushaway, BrushawayBody, BrushawayFooter, BrushawayHeader, InlineNotification, Loader } from '@ui';
import { Text } from '@ui/atoms/Text';
import { Tag } from '@ui/atoms/Tag';
import { TextInput } from '@ui/molecules/inputs/form/TextInput';
import { Checkbox } from '@ui/molecules/inputs/Checkbox';
import useDebounce from '@hooks/useDebounce';
import { translate } from '@utils/i18n';
import { getAllowlistUsers, updateAllowlist } from '../../services/CustomerFileDropService';
import { QUERY_KEYS } from '../../hooks/queryKeys';
import './AllowlistPanelStyles.scss';
const namespace = 'features.settings.customer_file_drop';
function AllowlistPanel({ allowedUserIds, onSaved }) {
    var _a;
    const [selectedIds, setSelectedIds] = useState(allowedUserIds);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const { data, isLoading, isError } = useQuery({
        queryKey: QUERY_KEYS.allowlistUsers(debouncedSearch),
        queryFn: () => getAllowlistUsers(debouncedSearch || undefined)
    });
    const users = (_a = data === null || data === void 0 ? void 0 : data.users) !== null && _a !== void 0 ? _a : [];
    const badgeLabel = translate('allowlist_badge', { namespace, count: selectedIds.length });
    const toggleUser = (user, checked) => {
        setSelectedIds((prev) => (checked ? [...new Set([...prev, user.id])] : prev.filter((id) => id !== user.id)));
    };
    const handleSave = () => __awaiter(this, void 0, void 0, function* () {
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        try {
            yield updateAllowlist(selectedIds);
            onSaved(selectedIds);
            setSaveSuccess(true);
        }
        catch (_a) {
            setSaveError(translate('allowlist_error', { namespace }));
        }
        finally {
            setIsSaving(false);
        }
    });
    return (React.createElement(Brushaway, { minWidth: 420, closeOnClickOutside: true, anchor: ({ onClick }) => (React.createElement(Button, { variant: "secondary", onClick: onClick, "data-testid": "manage-allowlist-button", "aria-label": translate('allowlist_heading', { namespace }) },
            React.createElement("i", { className: "fa-light fa-users", "aria-hidden": "true" }),
            translate('allowlist_heading', { namespace }),
            React.createElement(Tag, { label: badgeLabel }))) },
        React.createElement(BrushawayHeader, { title: translate('allowlist_heading', { namespace }), secondaryActions: React.createElement(Tag, { label: badgeLabel }) }),
        React.createElement(BrushawayBody, null,
            React.createElement("div", { className: "allowlist-panel", "data-testid": "allowlist-panel" },
                React.createElement(TextInput, { id: "customer-file-drop-allowlist-search", label: translate('allowlist_search_placeholder', { namespace }), value: search, onChange: (e) => setSearch(e.target.value) }),
                React.createElement("div", { className: "allowlist-panel__user-list", role: "group", "aria-label": translate('allowlist_heading', { namespace }) },
                    isLoading && (React.createElement("div", { className: "allowlist-panel__loading" },
                        React.createElement(Loader, { size: "32" }),
                        React.createElement(Text, { variant: "body" }, translate('allowlist_loading', { namespace })))),
                    !isLoading && isError && React.createElement(Text, { variant: "body" }, translate('loading_error', { namespace })),
                    !isLoading && !isError && users.length === 0 && (React.createElement(Text, { variant: "body" }, translate('allowlist_no_results', { namespace }))),
                    !isLoading &&
                        !isError &&
                        users.map((user) => (React.createElement("div", { key: user.id, className: "allowlist-panel__user-row" },
                            React.createElement(Checkbox, { id: `customer-file-drop-user-${user.id}`, checked: selectedIds.includes(user.id), onChange: (checked) => toggleUser(user, checked), label: React.createElement("span", { className: "allowlist-panel__user-info" },
                                    React.createElement(Text, { variant: "body" }, user.name),
                                    React.createElement(Text, { variant: "x-small" }, user.username)) }),
                            React.createElement(Tag, { label: user.role }))))),
                saveError ? (React.createElement(InlineNotification, { variant: "error", title: saveError, children: React.createElement("span", null, saveError) })) : null,
                saveSuccess ? (React.createElement(InlineNotification, { variant: "success", title: translate('allowlist_saved', { namespace }), children: React.createElement("span", null, translate('allowlist_saved', { namespace })) })) : null)),
        React.createElement(BrushawayFooter, null,
            React.createElement(Button, { onClick: () => void handleSave(), disabled: isSaving }, translate('allowlist_save', { namespace })))));
}
export default AllowlistPanel;
